/**
 * Animation Constants
 * Centralized animation variants for Framer Motion
 * Use these across the app for consistent animations
 */

import type { Variants, Transition } from "framer-motion";

// ============================================================================
// EASING CURVES
// ============================================================================

/**
 * Standard easing curves for consistent motion feel
 * Based on Material Design motion principles
 */
export const EASING = {
  /** Standard easing - for most animations */
  standard: [0.4, 0, 0.2, 1] as const,
  /** Ease out - for entering elements */
  easeOut: [0, 0, 0.2, 1] as const,
  /** Ease in - for exiting elements */
  easeIn: [0.4, 0, 1, 1] as const,
  /** Ease in-out - for moving elements */
  easeInOut: [0.4, 0, 0.2, 1] as const,
  /** Sharp - for quick, responsive interactions */
  sharp: [0.4, 0, 0.6, 1] as const,
  /** Bounce - for playful, attention-grabbing animations */
  bounce: [0.68, -0.55, 0.265, 1.55] as const,
} as const;

// ============================================================================
// DURATION CONSTANTS
// ============================================================================

/**
 * Standard durations in seconds
 */
export const DURATION = {
  /** Extra fast - 100ms, for micro-interactions */
  fastest: 0.1,
  /** Fast - 150ms, for small UI changes */
  fast: 0.15,
  /** Normal - 200ms, for standard transitions */
  normal: 0.2,
  /** Medium - 300ms, for larger transitions */
  medium: 0.3,
  /** Slow - 400ms, for emphasis */
  slow: 0.4,
  /** Slower - 500ms, for dramatic effects */
  slower: 0.5,
} as const;

// ============================================================================
// STANDARD TRANSITIONS
// ============================================================================

/**
 * Pre-configured transitions for common use cases
 */
export const TRANSITION = {
  /** Fast, snappy transition */
  fast: {
    duration: DURATION.fast,
    ease: EASING.standard,
  } satisfies Transition,

  /** Standard transition */
  default: {
    duration: DURATION.normal,
    ease: EASING.standard,
  } satisfies Transition,

  /** Smooth enter transition */
  enter: {
    duration: DURATION.medium,
    ease: EASING.easeOut,
  } satisfies Transition,

  /** Smooth exit transition */
  exit: {
    duration: DURATION.normal,
    ease: EASING.easeIn,
  } satisfies Transition,

  /** Spring-like transition */
  spring: {
    type: "spring",
    stiffness: 300,
    damping: 30,
  } satisfies Transition,

  /** Bounce transition */
  bounce: {
    type: "spring",
    stiffness: 400,
    damping: 10,
  } satisfies Transition,
} as const;

// ============================================================================
// COLLAPSIBLE / EXPANDABLE VARIANTS
// ============================================================================

/**
 * Variants for collapsible/expandable content
 * Use with AnimatePresence for smooth height animations
 */
export const collapsibleVariants: Variants = {
  hidden: {
    opacity: 0,
    height: 0,
    transition: {
      duration: DURATION.normal,
      ease: EASING.easeIn,
    },
  },
  visible: {
    opacity: 1,
    height: "auto",
    transition: {
      duration: DURATION.medium,
      ease: EASING.easeOut,
    },
  },
};

/**
 * Compact collapsible variant for faster animations
 */
export const collapsibleFastVariants: Variants = {
  hidden: {
    opacity: 0,
    height: 0,
    transition: {
      duration: DURATION.fast,
      ease: EASING.easeIn,
    },
  },
  visible: {
    opacity: 1,
    height: "auto",
    transition: {
      duration: DURATION.normal,
      ease: EASING.easeOut,
    },
  },
};

// ============================================================================
// FADE VARIANTS
// ============================================================================

/**
 * Simple fade in/out
 */
export const fadeVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: TRANSITION.default,
  },
  exit: {
    opacity: 0,
    transition: TRANSITION.exit,
  },
};

/**
 * Fade with scale effect
 */
export const fadeScaleVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: TRANSITION.enter,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: TRANSITION.exit,
  },
};

/**
 * Fade with scale for buttons/icons
 */
export const fadeScaleSmallVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.8,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: TRANSITION.fast,
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    transition: { duration: DURATION.fast },
  },
};

// ============================================================================
// SLIDE VARIANTS
// ============================================================================

/**
 * Slide from left
 */
export const slideLeftVariants: Variants = {
  hidden: {
    opacity: 0,
    x: -20,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: TRANSITION.enter,
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: TRANSITION.exit,
  },
};

/**
 * Slide from right
 */
export const slideRightVariants: Variants = {
  hidden: {
    opacity: 0,
    x: 20,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: TRANSITION.enter,
  },
  exit: {
    opacity: 0,
    x: 20,
    transition: TRANSITION.exit,
  },
};

/**
 * Slide from top
 */
export const slideUpVariants: Variants = {
  hidden: {
    opacity: 0,
    y: -20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: TRANSITION.enter,
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: TRANSITION.exit,
  },
};

/**
 * Slide from bottom
 */
export const slideDownVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: TRANSITION.enter,
  },
  exit: {
    opacity: 0,
    y: 20,
    transition: TRANSITION.exit,
  },
};

// ============================================================================
// STAGGER VARIANTS (for lists)
// ============================================================================

/**
 * Container variant for staggered children
 * Use with staggerItemVariants for list items
 */
export const staggerContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
};

/**
 * Fast stagger container (shorter delays)
 */
export const staggerContainerFastVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03,
      delayChildren: 0.05,
    },
  },
};

/**
 * Item variant for staggered lists - slide from left
 */
export const staggerItemVariants: Variants = {
  hidden: {
    opacity: 0,
    x: -10,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: TRANSITION.default,
  },
};

/**
 * Item variant with custom index-based delay
 * Usage: custom={index} on motion element
 */
export const staggerItemCustomVariants: Variants = {
  hidden: {
    opacity: 0,
    x: -10,
  },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: i * 0.03,
      duration: DURATION.normal,
      ease: EASING.easeOut,
    },
  }),
};

/**
 * Item variant for vertical lists - slide from bottom
 */
export const staggerItemVerticalVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 10,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: TRANSITION.default,
  },
};

// ============================================================================
// ROTATION VARIANTS
// ============================================================================

/**
 * Rotation for chevrons/arrows (180 degrees)
 * Use with animate prop: animate={{ rotate: isOpen ? 180 : 0 }}
 */
export const rotateChevronTransition: Transition = {
  duration: DURATION.normal,
  ease: EASING.standard,
};

/**
 * Rotation variants for toggleable icons
 */
export const rotateVariants: Variants = {
  collapsed: { rotate: 0 },
  expanded: { rotate: 180 },
};

/**
 * Rotation 90 degrees (for sidebar toggles)
 */
export const rotate90Variants: Variants = {
  collapsed: { rotate: 0 },
  expanded: { rotate: 90 },
};

// ============================================================================
// MODAL / OVERLAY VARIANTS
// ============================================================================

/**
 * Backdrop/overlay fade
 */
export const overlayVariants: Variants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: DURATION.normal,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: DURATION.fast,
    },
  },
};

/**
 * Modal content animation
 */
export const modalVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 10,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: TRANSITION.enter,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 10,
    transition: TRANSITION.exit,
  },
};

// ============================================================================
// SIDEBAR / DRAWER VARIANTS
// ============================================================================

/**
 * Sidebar slide from left
 */
export const sidebarLeftVariants: Variants = {
  hidden: {
    x: "-100%",
    opacity: 0,
  },
  visible: {
    x: 0,
    opacity: 1,
    transition: TRANSITION.enter,
  },
  exit: {
    x: "-100%",
    opacity: 0,
    transition: TRANSITION.exit,
  },
};

/**
 * Sidebar slide from right
 */
export const sidebarRightVariants: Variants = {
  hidden: {
    x: "100%",
    opacity: 0,
  },
  visible: {
    x: 0,
    opacity: 1,
    transition: TRANSITION.enter,
  },
  exit: {
    x: "100%",
    opacity: 0,
    transition: TRANSITION.exit,
  },
};

// ============================================================================
// TOOLTIP / POPOVER VARIANTS
// ============================================================================

/**
 * Tooltip appearing from bottom
 */
export const tooltipVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 5,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: TRANSITION.fast,
  },
  exit: {
    opacity: 0,
    y: 5,
    scale: 0.95,
    transition: { duration: DURATION.fastest },
  },
};

// ============================================================================
// BUTTON / INTERACTION VARIANTS
// ============================================================================

/**
 * Button tap/press animation
 * Use with whileTap prop
 */
export const buttonTapVariants = {
  scale: 0.98,
};

/**
 * Button hover animation
 * Use with whileHover prop
 */
export const buttonHoverVariants = {
  scale: 1.02,
};

/**
 * Icon button animation (more pronounced)
 */
export const iconButtonVariants = {
  tap: { scale: 0.9 },
  hover: { scale: 1.1 },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Create a stagger delay based on index
 * @param index - Item index in list
 * @param baseDelay - Base delay in seconds (default: 0.03)
 */
export const getStaggerDelay = (index: number, baseDelay = 0.03): number =>
  index * baseDelay;

/**
 * Create custom transition with duration
 * @param duration - Duration in seconds
 */
export const createTransition = (duration: number): Transition => ({
  duration,
  ease: EASING.standard,
});
